import {
  Button,
  DialogForm,
  Form,
  Label,
  useDialogExitWarning,
} from '@ais-ui/ui-core';
import React, { useMemo } from 'react';
import { MutatePhysicalForm } from './MutatePhysicalForm';
import { cn } from '@ais-ui/utils';
import {
  DrawingStyles,
  useDrawingContext,
  useResetDrawingContext,
} from '@ais-ui/react-openlayers';
import { useLocalization } from '@ais-ui/locale-management';
import { useGetShareGroups } from '@ais-ui/websockets';
import { PiDiamond } from 'react-icons/pi';
import { useAtonContext } from '../AtonContext';
import { useDrawWithViewMode } from '../../../hooks/useDrawWithViewMode';

interface SavePhysicalAtonDialogProps {
  mutateType: 'create' | 'edit';
}

export const SavePhysicalAtonDialog = React.memo(
  ({ mutateType }: SavePhysicalAtonDialogProps) => {
    const {
      showDialog,
      setShowDialog,
      isSubmitting,
      physicalForm: form,
      onCreatePhysical,
      onUpdatePhysical,
      onSelectAtonOnMap,
      aton,
    } = useAtonContext();
    const { messages } = useLocalization();
    const dialogTitle =
      mutateType === 'create'
        ? messages.atonPhysicalDialogCreateTitle
        : messages.atonPhysicalDialogEditTitle;

    const onSubmit = useMemo(() => {
      if (mutateType === 'create') {
        return onCreatePhysical;
      }
      return onUpdatePhysical;
    }, [mutateType, onCreatePhysical, onUpdatePhysical]);

    const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();
    const { showDrawPanel } = useDrawingContext();
    const resetDrawingContext = useResetDrawingContext();

    const { onStartDrawingFromDialog } = useDrawWithViewMode(
      form,
      setShowDialog,
      'geometry',
      DrawingStyles.aton,
    );

    const handleOpenChange = (isOpen: boolean) => {
      setShowDialog(isOpen);
    };

    const handleConfirm = React.useCallback(() => {
      if (showDrawPanel) {
        resetDrawingContext();
      }
      setShowDialog(false);
    }, [showDrawPanel, resetDrawingContext, setShowDialog]);

    const { handleClose: handleExitWarning, ExitWarningDialog } =
      useDialogExitWarning({
        onConfirm: handleConfirm,
        title: messages.atonWarningTitle || 'Discard AtoN changes?',
        description:
          messages.atonWarningDescription ||
          'Your AtoN configuration will be lost. Are you sure you want to close?',
      });

    return (
      <Form {...form}>
        <DialogForm
          showDialog={showDialog}
          onOpenChange={handleOpenChange}
          title={dialogTitle}
          isLoading={isLoadingGroups}
          hasLoaded={!isLoadingGroups && !!groups}
          buttonId="submit-physical-aton-button"
          buttonText={
            isSubmitting ? messages.genericSubmitting : messages.genericSubmit
          }
          buttonDisabled={isSubmitting}
          buttonOnClick={form.handleSubmit(onSubmit)}
          onClose={handleExitWarning}
        >
          <div className="space-y-6">
            <div
              className={cn(
                'flex flex-row items-center justify-between border rounded-lg p-4',
              )}
            >
              <div className="space-y-0.5">
                <Label className="text-base">
                  {messages.atonPhysicalDialogSelectAtonLabel}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {messages.atonPhysicalDialogFormDescriptionPlaceholder}
                </p>
              </div>
              <Button variant="outline" size="icon" onClick={onSelectAtonOnMap}>
                <PiDiamond className="size-5" />
              </Button>
            </div>

            <MutatePhysicalForm
              form={form}
              onStartDrawing={onStartDrawingFromDialog}
              aton={aton ?? undefined}
              mutateType={mutateType}
            ></MutatePhysicalForm>
          </div>
        </DialogForm>
        <ExitWarningDialog />
      </Form>
    );
  },
);
